<script setup lang="ts">
import { storeToRefs } from 'pinia'
import debounce from 'lodash/debounce'
import { Link } from '~/lib/services/content/content.shared.dtos'
import { GTMMenuType } from '~/lib/models/gtm'

const props = defineProps<{
  isLanding?: boolean
  stickyLanding?: boolean
}>()

const { t } = useLocale()

const layoutStore = useLayoutStore()
const authStore = useAuthStore()
const appStore = useAppStore()
const { isUserLogged } = storeToRefs(authStore)
const restaurantStore = useRestaurantStore()
const { menuProductsPath } = useRestaurantActions()
const site = useSite()
const { cartRoute, ssoRoute, downloadAppRoute, profileRoute } = useMcdRoutes()
const { trackGoLogin, trackMenuClick } = useGTM()
const { windowHeight } = useResizedWindowHeight()

const isMobile = inject<Ref<boolean>>('isMobile')
const contentCart = { tKey: 'onboarding.stepCart', action: true, isLast: false }
const contentAccount = { tKey: 'onboarding.stepAccount', action: true, isLast: false }

const setAppendToLargeScreen = computed(() => {
  if (process.client) {
    return windowHeight.value > 600 ? '#header-tooltip' : 'body'
  }
})

const header = site.header
const origin = useSiteDomain({ protocol: true, noOverride: true })
const stickyHeader = ref(false)

if (!header) throw new Error('Header not present')

const productsSection = computed(() => {
  const link: Link = {
    title: t('routes.staticMenu.linkTitle'),
    url: '',
    blank: false,
    download: false,
    action: null,
    page: null,
    icon: null,
  }
  link.url = menuProductsPath.value || ''
  return link
})

const siteNavigationElements = computed(() => {
  if (props.isLanding || !isMobile?.value) {
    const links = [productsSection.value, ...header.mainLinks].map(link => ({
      '@context': 'https://schema.org',
      '@type': 'SiteNavigationElement',
      '@id': '#header',
      'name': link.title || '',
      'url': `${origin}${link.page?.path || link.url || ''}`,
    }))
    return links
  }
  return []
})

const anyMobileContent = computed(() => {
  if (props.isLanding) return true
  if (restaurantStore.isRestaurantSelected) return true
  return false
})

const openCart = async (ev: PointerEvent, toggle: Function) => {
  if (isMobile?.value) return await navigateTo(cartRoute())
  toggle(ev)
}

const openOverlayDesktop = async (tg: HTMLElement, show: Function) => {
  if (!isMobile?.value) {
    const route = useRoute()
    // on cart when adding promos or suggestive dont open overlay
    if (route.name === 'orders-cart') return
    let ev = new PointerEvent('PointerEvent', { bubbles: false })
    ev = {
      ...ev,
      target: tg,
      currentTarget: tg,
    }
    show(ev)
  }
}

const goToIm = (isRegister: boolean) => {
  const route = ssoRoute({ register: isRegister })
  return route
}

const onScroll = () => {
  stickyHeader.value = window.scrollY > 0
}

const onScrollDebounced = debounce(onScroll, 250)

const toggleProfileTooltip = (toggleFn: Function, isVisible: boolean) => {
  // track event when is opening profile tooltip
  if (!isVisible) {
    trackMenuClick(GTMMenuType.MY_ACCOUNT, 'my_account')
  }
  toggleFn()
}

useJsonld(() => {
  if (!siteNavigationElements.value.length) return null
  return {
    '@context': 'https://schema.org',
    '@graph': siteNavigationElements.value,
  } as any
})

onMounted(() => {
  if (process.client && !props.isLanding) {
    window.addEventListener('scroll', onScrollDebounced)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScrollDebounced, false)
})
</script>

<template>
  <div class="left-0 top-0 z-10" :class="{ sticky: (isLanding && stickyLanding) || !isLanding }">
    <!-- Smartbanner -->
    <ClientOnly>
      <LazySmartbanner
        v-if="layoutStore.smartbanner && !isLanding"
        :config="layoutStore.smartbanner"
      />
    </ClientOnly>

    <!-- Header -->
    <div
      data-test="mcd-header"
      class="duration-250 relative h-fit bg-transparent transition-all ease-out md:h-[180px]"
      :class="{
        '!bg-white md:shadow-md-extended': !isLanding,
        'border border-transparent': anyMobileContent,
        'md:!h-[8rem]': stickyHeader || stickyLanding,
      }"
    >
      <div
        class="mx-auto md:mx-14 md:my-0"
        :class="{ 'my-2 ml-7': anyMobileContent, 'mr-4': isLanding }"
      >
        <header class="mx-auto flex h-fit items-center justify-between gap-2 md:min-h-[8rem]">
          <div class="flex flex-1 items-center gap-2 md:flex-none">
            <button
              v-if="!isMobile || isLanding"
              type="button"
              class="block h-12 w-12 text-gray-500"
              @click="layoutStore.openMenuSideBar()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="22"
                viewBox="0 0 28 22"
                :fill="isLanding && !stickyLanding ? 'white' : '#292929'"
                class="mx-auto"
              >
                <path
                  d="M0 2.25C0 1.42157 0.671573 0.75 1.5 0.75H28V1.75C28 2.57843 27.3284 3.25 26.5 3.25L0 3.25V2.25Z"
                />
                <path
                  d="M28 11.25C28 10.4216 27.3284 9.75 26.5 9.75L0 9.75V10.75C0 11.5784 0.671573 12.25 1.5 12.25L28 12.25V11.25Z"
                />
                <path
                  d="M1.5 18.75C0.671573 18.75 0 19.4216 0 20.25V21.25H26.5C27.3284 21.25 28 20.5784 28 19.75V18.75H1.5Z"
                />
              </svg>
            </button>
            <div v-if="!isMobile || isLanding" class="grid h-12 w-12 place-content-center">
              <router-link to="/">
                <svg
                  width="36"
                  height="32"
                  viewBox="0 0 36 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M25.8749 2.48878C28.9812 2.48878 31.4983 15.5745 31.4983 31.7156H36C36 14.3315 31.4663 0.240234 25.8749 0.240234C22.6935 0.240234 19.8555 4.47621 18 11.0982C16.1445 4.47621 13.3065 0.240234 10.1251 0.240234C4.53509 0.240234 0 14.3315 0 31.7156H4.50174C4.50174 15.5745 7.02015 2.48878 10.1251 2.48878C13.2314 2.48878 15.7498 14.5676 15.7498 29.4671H20.2502C20.2502 14.5676 22.7686 2.48878 25.8749 2.48878Z"
                    fill="#FFBC0D"
                  />
                </svg>
              </router-link>
            </div>
            <div v-if="restaurantStore.isRestaurantSelected" class="ml-0 w-full md:ml-10 md:w-fit">
              <StatusBarRestaurant :showing-onboarding="layoutStore.onboarding" />
            </div>
          </div>

          <ClientOnly>
            <div
              id="header-tooltip"
              class="flex flex-wrap-reverse items-center justify-center gap-2 md:p-0"
            >
              <template v-if="restaurantStore.isRestaurantSelected && !isMobile">
                <!-- Cart must not be visible if not has restaurant selected -->
                <PrimeTooltip
                  :dismissable="true"
                  append-value="#header-tooltip"
                  landing-class="!top-[83px]"
                >
                  <template #actionElement="{ toggle, show }">
                    <PopperWrapper data-popper="stepCart" placement="bottom">
                      <CartBag
                        @click="(ev: PointerEvent) => openCart(ev, toggle)"
                        @on-add="(tg: HTMLElement) => openOverlayDesktop(tg, show)"
                      />
                      <template #content="{ close }">
                        <McdPopper :close="close" :content="contentCart" />
                      </template>
                    </PopperWrapper>
                  </template>
                  <template #tooltipContent="{ overlayClosed }">
                    <CartOverlay @overlay-closed="overlayClosed" />
                  </template>
                </PrimeTooltip>
              </template>

              <template v-if="!isUserLogged">
                <template v-if="appStore.isEcommerceDisabled">
                  <NuxtLink data-test="header-donwload-app" :to="downloadAppRoute()">
                    <McdButton is-primary class="hidden md:block md:h-[40px] md:w-[153px] md:py-2">
                      {{ t('profile.downloadApp') }}
                    </McdButton>
                  </NuxtLink>
                </template>
                <template v-else>
                  <!-- Button login/register -->
                  <NuxtLink :to="goToIm(false)" @click="trackGoLogin">
                    <McdButton is-primary class="hidden md:block md:h-[40px] md:w-[153px] md:py-2">
                      {{ t('auth.logOnTo') }}
                    </McdButton>
                  </NuxtLink>

                  <!-- mainButton MOBILE -->
                  <NuxtLink v-if="isMobile && isLanding" :to="goToIm(false)" @click="trackGoLogin">
                    <McdButton
                      is-primary
                      class="h-[40px] w-[137px] py-1"
                      :class="isLanding ? 'block' : 'hidden'"
                    >
                      {{ t('auth.logOnTo') }}
                    </McdButton>
                  </NuxtLink>
                </template>
              </template>

              <template v-if="isUserLogged && !isMobile">
                <PrimeTooltip
                  :append-value="setAppendToLargeScreen"
                  landing-class="!top-[83px] !p-3"
                >
                  <template #actionElement="{ toggle, isVisible }">
                    <PopperWrapper data-popper="stepAccount" placement="bottom">
                      <McdButton
                        is-primary
                        class="h-[40px] w-[153px]"
                        data-test="user-profile-button"
                        @on-click="
                          (ev: PointerEvent) =>
                            toggleProfileTooltip(() => toggle(ev), isVisible)
                        "
                      >
                        <div class="flex h-2 items-center justify-center gap-4">
                          <img class="h-4 w-4" src="/images/icons/profileIcon.svg" alt="user" />
                          <p class="line-clamp-1">
                            {{
                              `${
                                authStore.userMetadata?.firstname
                                  ? authStore.userMetadata?.firstname
                                  : t('profile.title')
                              }`
                            }}
                          </p>
                        </div>
                      </McdButton>
                      <template #content="{ close }">
                        <McdPopper :close="close" :content="contentAccount" />
                      </template>
                    </PopperWrapper>
                  </template>
                  <template #tooltipContent="{ overlayClosed }">
                    <ProfileTooltip @overlay-closed="overlayClosed" />
                  </template>
                </PrimeTooltip>
              </template>
              <template v-else-if="isUserLogged && isMobile && isLanding">
                <NuxtLink
                  :to="profileRoute()"
                  @click="trackMenuClick(GTMMenuType.MY_ACCOUNT, 'my_account')"
                >
                  <McdButton is-primary class="h-[40px] w-[153px]" data-test="user-profile-button">
                    <div class="flex h-2 items-center justify-center gap-4">
                      <img class="h-4 w-4" src="/images/icons/profileIcon.svg" alt="user" />
                      <p class="line-clamp-1">
                        {{
                          `${
                            authStore.userMetadata?.firstname
                              ? authStore.userMetadata?.firstname
                              : t('profile.title')
                          }`
                        }}
                      </p>
                    </div>
                  </McdButton>
                </NuxtLink>
              </template>
            </div>
          </ClientOnly>
        </header>

        <div
          v-if="(isLanding && !stickyLanding) || (!isLanding && !stickyHeader)"
          class="mx-14 hidden min-h-10 gap-2 text-lg md:flex md:flex-row md:items-center md:gap-12"
          :class="isLanding ? 'text-white' : 'text-mcd-black'"
        >
          <!-- products link is static: static products on restaurant menu for area -->
          <HeaderSubMenu :section="productsSection" />
          <div v-for="(section, index) in header.mainLinks" :key="`top-${index}`">
            <HeaderSubMenu :section="section" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
